import { type FormEvent, useRef, useState } from 'react'
import { motion } from 'motion/react'
import { ArrowRight, Check, ShieldCheck } from 'lucide-react'
import { createBetaRegistration } from '../../api/betaRegistrationApi'
import { ApiError } from '../../api/httpClient'
import type { BetaUsage } from '../../models/BetaRegistration'
import type { LandingCopy, Language } from '../../models/LandingPage'
import { trackEvent } from '../../utils/analytics'
import { betaUsageOptions, hardware } from '../../utils/landingContent'
import { SocialLinks } from '../common/SocialLinks'

type FoundingBetaSectionProps = {
  copy: LandingCopy
  language: Language
  reveal: object
}

export function FoundingBetaSection({ copy, language, reveal }: FoundingBetaSectionProps) {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const formStarted = useRef(false)
  const submissionInFlight = useRef(false)

  function handleFormStart() {
    if (formStarted.current) {
      return
    }

    formStarted.current = true
    trackEvent('beta_form_started')
  }

  async function handleBetaSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submissionInFlight.current) {
      return
    }

    submissionInFlight.current = true
    setSubmitting(true)
    setSubmitError('')
    trackEvent('beta_form_submitted')

    const formData = new FormData(event.currentTarget)
    const fullName = String(formData.get('name') ?? '').trim()
    const [firstName = '', ...lastNameParts] = fullName.split(/\s+/).filter(Boolean)

    try {
      await createBetaRegistration({
        firstName,
        lastName: lastNameParts.length ? lastNameParts.join(' ') : null,
        email: String(formData.get('email') ?? '').trim(),
        country: String(formData.get('country') ?? '').trim(),
        hardware: String(formData.get('hardware') ?? '').trim(),
        usage: String(formData.get('usage') ?? 'Home') as BetaUsage,
        language,
        betaAccepted: formData.get('betaAccepted') === 'on',
      })
      setSubmitted(true)
      trackEvent('beta_registration_success')
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.kind === 'conflict') {
          setSubmitError("You're already on the Founding Beta waiting list.")
          trackEvent('beta_registration_duplicate')
        } else if (error.kind === 'validation') {
          setSubmitError(error.message)
          trackEvent('beta_registration_error', { status: error.status ?? 400, kind: error.kind })
        } else if (error.kind === 'network') {
          setSubmitError('Unable to contact ToneSignature servers.')
          trackEvent('beta_registration_error', { kind: error.kind })
        } else {
          setSubmitError('Something went wrong while joining the Founding Beta.')
          trackEvent('beta_registration_error', { status: error.status ?? 500, kind: error.kind })
        }
      } else {
        setSubmitError('Something went wrong while joining the Founding Beta.')
        trackEvent('beta_registration_error', { kind: 'unknown' })
      }
    } finally {
      setSubmitting(false)
      submissionInFlight.current = false
    }
  }

  return <section id="beta" className="section beta">
    <div className="container beta-shell">
      <motion.div {...reveal} className="beta-copy">
        <span className="beta-label"><ShieldCheck/> FOUNDING BETA</span>
        <h2>{copy.betaTitle}</h2>
        <p>{copy.betaSub}</p>
        <div className="beta-benefits">
          <div><b>01</b><span>Early product access</span></div>
          <div><b>02</b><span>Direct influence on development</span></div>
          <div><b>03</b><span>Founder recognition</span></div>
        </div>
      </motion.div>
      <motion.form {...reveal} className="beta-form" onSubmit={handleBetaSubmit} onFocusCapture={handleFormStart}>
        {submitted ? <div className="success" role="status">
          <div><Check/></div>
          <h3>{copy.sent}</h3>
          <p>We will contact you when the first private testing group opens.</p>
        </div> : <>
          <div className="form-row">
            <label>Name<input required name="name" placeholder="Your name" autoComplete="name" disabled={submitting}/></label>
            <label>Email<input required name="email" type="email" placeholder="you@email.com" autoComplete="email" disabled={submitting}/></label>
          </div>
          <div className="form-row">
            <label>Country<input required name="country" placeholder="Italy" autoComplete="country-name" disabled={submitting}/></label>
            <label>Hardware<select required name="hardware" defaultValue="" disabled={submitting}><option value="" disabled>Select modeler</option>{hardware.map(h => <option key={h.name} value={h.value ?? h.name}>{h.name}</option>)}</select></label>
          </div>
          <fieldset>
            <legend>Primary use</legend>
            <div className="use-grid">
              {betaUsageOptions.map((option, i) => <label key={option.value}><input type="radio" name="usage" value={option.value} defaultChecked={i === 0} disabled={submitting}/> {option.label}</label>)}
            </div>
          </fieldset>
          <label className="check"><input required name="betaAccepted" type="checkbox" disabled={submitting}/> I understand this is a beta.</label>
          {submitError && <p className="form-error" role="alert">{submitError}</p>}
          <button className="button form-button" type="submit" disabled={submitting} aria-busy={submitting}>{submitting && <span className="spinner" aria-hidden="true"/>}{submitting ? 'Submitting...' : copy.submit}{!submitting && <ArrowRight size={17}/>}</button>
          <SocialLinks label="Follow ToneSignature" className="beta-social"/>
          <small className="privacy-note">By joining the beta you agree to our <a href="/privacy">Privacy Policy</a>.</small>
          <small className="no-obligation">{copy.noObligation}</small>
        </>}
      </motion.form>
    </div>
  </section>
}
